import { api } from "@service/api";
import { Button } from "../../buttons/button";
import { Input } from "../../forms/input";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaQuestionCircle,
  FaSearch,
  FaSignOutAlt,
  FaUserCircle,
} from "react-icons/fa";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { useAuthContext } from "@hooks/contexts/auth-context";

// Images
// import logo from "@assets/img/logo/logo-02.png";
import logo from "@assets/img/logo/logo-tegrapharmacorp.webp";
import avatar from "@assets/img/defaultAvatar.png";
import { formatName } from "@hooks/functions/formatName";
import { FaScrewdriverWrench } from "react-icons/fa6";

export const Header = () => {
  const navigate = useNavigate();
  const { customer } = ZuAuthStore();
  const { signOut } = useAuthContext();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [isManager, setIsManager] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showMobileSearch, setShowMobileSearch] = useState<boolean>(false);

  const [trackingCode, setTrackingCode] = useState<string>("");
  const [trackingCodeError, setTrackingCodeError] = useState<string>("");
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);

  // Refs for detecting clicks outside
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const desktopSearchRef = useRef<HTMLDivElement>(null);

  // Get first name from customer
  const getFirstName = () => {
    if (!customer?.customer?.name) return "";
    return formatName(customer.customer.name.split(" ")[0]);
  };

  useEffect(() => {
    const manager = customer?.customer?.access?.management || false;
    setIsManager(manager);
  }, [customer]);

  const handleSubmit = async () => {
    if (!trackingCode) {
      return setTrackingCodeError(
        `É necessário digitar um código de rastreio.`,
      );
    }

    try {
      const params = `?codeId=${trackingCode}`;
      const result = await api.get(`/misc/tracking/code${params}`);
      const tracking = result.data;

      if (!tracking) {
        return pushToastify({
          message: "Código não localizado, verifique e tente novamente.",
          type: "info",
          theme: "dark",
          pauseOnHover: true,
        });
      }

      pushToastify({
        message: "Pedido localizado com sucesso, redirecionamento iniciado...",
        type: "success",
        theme: "dark",
        pauseOnHover: true,
      });

      setTimeout(() => {
        window.location.href = `/tracking/${trackingCode}`;
      }, 750);
    } catch (error: any) {
      console.log({ error });
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Houve um erro ao consultar o código informado!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showUserMenu) setShowUserMenu(false);
        if (showMobileSearch) setShowMobileSearch(false);
        if (showSearch) setShowSearch(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showUserMenu, showMobileSearch, showSearch]);

  // Handle click outside for user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  // Handle click outside for mobile search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target as Node)
      ) {
        setShowMobileSearch(false);
      }
    };

    if (showMobileSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMobileSearch]);

  // Handle click outside for desktop search (optional - closes when clicking outside the search area)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        desktopSearchRef.current &&
        !desktopSearchRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false);
        setTrackingCodeError(""); // Clear error when closing
        setTrackingCode(""); // Clear input when closing
      }
    };

    if (showSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearch]);

  return (
    <div
      className="container-fluid"
      style={{
        backgroundColor: "var(--hf-color)",
        paddingLeft: "clamp(1rem, 5vw, 10rem)",
        paddingRight: "clamp(1rem, 5vw, 10rem)",
        // Just in Mobile
        paddingTop: window.innerWidth < 768 ? "1rem" : "0.3rem",
        paddingBottom: window.innerWidth < 768 ? "1rem" : "0.3rem",
      }}
    >
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
        {/* Logo Section */}
        <div className="col-12 col-md-6 text-center text-md-start">
          <img
            src={logo}
            style={{
              height: "clamp(20px, 10vw, 35px)",
              cursor: "pointer",
            }}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </div>

        {/* Actions Section */}
        <div className="col-12 col-md-6">
          {/* MOBILE - Clean search-only trigger */}
          <div className="d-flex d-md-none flex-column gap-2">
            {/* Top action row */}
            <div className="d-flex align-items-center gap-2">
              {/* Search icon button */}
              <button
                className="btn btn-outline-info"
                style={{ height: "48px", width: "48px" }}
                onClick={() => setShowMobileSearch(true)}
                aria-label="Open search"
              >
                <FaSearch className="fs-4" />
              </button>

              {customer ? (
                <>
                  <Button
                    className="btn btn-info text-white px-3 py-3"
                    onClick={() => {
                      navigate("/faq");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    aria-label="faq"
                    style={{ height: "48px", width: "48px" }}
                  >
                    <FaQuestionCircle className="fs-4" />
                  </Button>

                  <div
                    className="flex-fill position-relative"
                    ref={userMenuRef}
                  >
                    <div
                      className="d-flex align-items-center h-100 w-100"
                      style={{
                        background: "#f8f9fa",
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: "2px solid #dee2e6",
                        padding: "8px",
                        minHeight: "48px",
                      }}
                      onClick={() => setShowUserMenu(!showUserMenu)}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          flexShrink: 0,
                          marginRight: "8px",
                        }}
                      >
                        <img
                          src={avatar}
                          alt="User"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      <div
                        className="flex-grow-1 overflow-hidden"
                        style={{ minWidth: 0 }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-1"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#0acf97",
                            }}
                          />
                          <div
                            className="fw-bold text-truncate"
                            style={{ fontSize: "14px" }}
                          >
                            {getFirstName()}
                          </div>
                        </div>
                        <div
                          className="text-muted text-truncate"
                          style={{
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {customer.customer.email}
                        </div>
                      </div>
                    </div>

                    {showUserMenu && (
                      <div
                        className="position-absolute w-100"
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          top: "100%",
                          left: 0,
                          background: "white",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          marginTop: "4px",
                        }}
                      >
                        <div className="p-3 border-bottom">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              style={{
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={avatar}
                                alt="User"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                            <div
                              className="flex-grow-1 overflow-hidden"
                              style={{ minWidth: 0 }}
                            >
                              <div className="fw-bold text-truncate">
                                {formatName(customer.customer.name)}
                              </div>
                              <div
                                className="text-muted small text-truncate"
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {customer.customer.email}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-2">
                          <button
                            className="btn btn-primary w-100 mb-2 d-flex align-items-center justify-content-center gap-2"
                            onClick={() => {
                              navigate("/dashboard");
                              setShowUserMenu(false);
                            }}
                          >
                            <FaUserCircle />
                            Dashboard
                          </button>
                          {isManager && (
                            <>
                              <button
                                className="btn btn-outline-secondary w-100 mb-2 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => {
                                  navigate("/management");
                                  setShowUserMenu(false);
                                }}
                              >
                                <FaScrewdriverWrench />
                                Gerenciamento
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={() => {
                              signOut();
                              setShowUserMenu(false);
                            }}
                          >
                            <FaSignOutAlt />
                            Sair
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Button
                    className="btn btn-info text-white flex-fill px-4 py-3"
                    onClick={() => {
                      navigate("/faq");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    <span className="fw-bold">FAQ</span>
                  </Button>
                  <Button
                    className="btn btn-default text-white flex-fill py-3"
                    onClick={() => navigate("/login")}
                  >
                    <span className="fw-bold">Login</span>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile search overlay */}
            {showMobileSearch && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-start"
                style={{
                  background: "rgba(0, 0, 0, 0.5)",
                  zIndex: 2000,
                  padding: "1rem",
                }}
                onClick={(e) => {
                  // Only close if clicking the backdrop, not the content
                  if (e.target === e.currentTarget) {
                    setShowMobileSearch(false);
                  }
                }}
              >
                <div
                  ref={mobileSearchRef}
                  className="bg-white p-3 rounded-3 shadow"
                  style={{ maxWidth: "100%" }}
                >
                  {/* Search header */}
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold">Buscar pedido</span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowMobileSearch(false)}
                      aria-label="Close search"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Search input */}
                  <Input
                    animated={true}
                    key="trackingCode-mobile"
                    name="trackingCode"
                    label="Nº do Pedido ou Código de Rastreio"
                    type="text"
                    className="mb-2"
                    autoFocus
                    onChange={(e) => {
                      const code = e.target.value;
                      setTrackingCode(code);
                      if (code.length > 0) {
                        setTrackingCodeError("");
                      }
                    }}
                    errorMessage={trackingCodeError}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSubmit();
                        setShowMobileSearch(false);
                      }
                    }}
                  />

                  {/* Search action */}
                  <Button
                    className="btn btn-info text-white w-100 py-2"
                    onClick={() => {
                      handleSubmit();
                      setShowMobileSearch(false);
                    }}
                  >
                    <span className="fw-bold">Buscar</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* DESKTOP - Side by side */}
          <div
            className="d-none d-md-flex justify-content-end align-items-center gap-1"
            ref={desktopSearchRef}
          >
            {/* Search container */}
            <div className="d-flex align-items-center position-relative">
              {/* Search icon button */}
              <button
                className="btn btn-info d-flex align-items-center justify-content-center"
                style={{ height: "48px", width: "48px" }}
                onClick={() => setShowSearch((prev) => !prev)}
                aria-label="Toggle search input"
              >
                <FaSearch />
              </button>

              {/* Animated search input */}
              <div
                className="ms-2"
                style={{
                  width: showSearch ? "280px" : "0px",
                  overflow: "hidden",
                  transition: "width 0.3s ease",
                }}
              >
                <div
                  style={{
                    opacity: showSearch ? 1 : 0,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <Input
                    animated={true}
                    key="trackingCode-desktop"
                    name="trackingCode"
                    label="Nº do Pedido ou Código de Rastreio"
                    type="text"
                    className="mb-0 mt-3"
                    onChange={(e) => {
                      const code = e.target.value;
                      setTrackingCode(code);
                      if (code.length > 0) {
                        setTrackingCodeError("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSubmit();
                        setShowSearch(false);
                      }
                    }}
                    errorMessage={trackingCodeError}
                  />
                </div>
              </div>
            </div>

            {/* Search action button */}
            <div
              style={{
                width: showSearch ? "auto" : "0px",
                overflow: "hidden",
                transition: "all 0.3s ease",
              }}
            >
              <Button
                className="btn btn-info text-white px-4 py-3 ms-2"
                onClick={() => {
                  handleSubmit();
                  setShowSearch(false);
                }}
                style={{
                  opacity: showSearch ? 1 : 0,
                  pointerEvents: showSearch ? "auto" : "none",
                  transition: "opacity 0.2s ease",
                }}
              >
                <span className="fw-bold">Buscar</span>
              </Button>
            </div>

            {/* User area remains unchanged */}
            {customer ? (
              <div className="d-flex align-items-center gap-3 position-relative">
                <Button
                  className="btn btn-info text-white px-4 py-3"
                  onClick={() => {
                    navigate("/faq");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <span className="fw-bold">FAQ</span>
                </Button>

                {/* Desktop user profile */}
                <div
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: "8px 16px",
                    background: "#f8f9fa",
                    borderRadius: "25px",
                    cursor: "pointer",
                    border: "2px solid #dee2e6",
                  }}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  {/* Avatar circle */}
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#f8f9fa",
                    }}
                  >
                    <img
                      src={avatar}
                      alt="User Avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>

                  {/* User name */}
                  <div>
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle mb-1 me-1"
                        style={{
                          width: "12px",
                          height: "12px",
                          backgroundColor: "#0acf97",
                        }}
                      />
                      <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                        {getFirstName()}
                      </div>
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {customer.customer.email}
                    </div>
                  </div>
                </div>

                {/* Desktop user menu */}
                {showUserMenu && (
                  <div
                    ref={userMenuRef}
                    className="position-absolute"
                    style={{
                      top: "100%",
                      right: 0,
                      background: "white",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      minWidth: "250px",
                      marginTop: "8px",
                    }}
                  >
                    <div className="p-3 border-bottom">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={avatar}
                            alt="User Avatar"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                        <div>
                          <div className="fw-bold">
                            {formatName(customer.customer.name)}
                          </div>
                          <div className="text-muted small">
                            {customer.customer.email}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <button
                        className="btn btn-primary w-100 mb-2 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => {
                          navigate("/dashboard");
                          setShowUserMenu(false);
                        }}
                      >
                        <FaUserCircle />
                        Dashboard
                      </button>
                      {isManager && (
                        <>
                          <button
                            className="btn btn-outline-secondary w-100 mb-2 d-flex align-items-center justify-content-center gap-2"
                            onClick={() => {
                              navigate("/management");
                              setShowUserMenu(false);
                            }}
                          >
                            <FaScrewdriverWrench />
                            Gerenciamento
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={() => {
                          signOut();
                          setShowUserMenu(false);
                        }}
                      >
                        <FaSignOutAlt />
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button
                  className="btn btn-info text-white px-4 py-3 me-2"
                  onClick={() => {
                    navigate("/faq");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <span className="fw-bold">FAQ</span>
                </Button>
                {/* <Button
                  className="btn btn-default text-white px-4 py-3"
                  onClick={() => navigate("/login")}
                >
                  <span className="fw-bold">Login</span>
                </Button> */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
